import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Explainer } from "../components/friendly";

export default function LinkInBio() {
  const { activeClientId, activeClient } = useApp();
  const [bio, setBio] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (activeClientId) api.get(`/clients/${activeClientId}/bio`).then((r) => setBio(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const save = async (next) => {
    setSaving(true);
    const b = next || bio;
    try {
      const { data } = await api.put(`/clients/${activeClientId}/bio`, { headline: b.headline, links: b.links, published: b.published });
      setBio(data);
      toast.success("Bio page saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const publicUrl = bio ? `${window.location.origin}/b/${bio.slug}` : "";
  const setLink = (i, k, v) => {
    const links = bio.links.map((l, idx) => (idx === i ? { ...l, [k]: v } : l));
    setBio({ ...bio, links });
  };

  if (!bio) return null;
  return (
    <div className="space-y-6 fade-up max-w-2xl" data-testid="bio-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Link-in-Bio</h1>
        <p className="text-sm text-slate-500 mt-1">A public micro-page for <b>{activeClient?.name}</b>'s social profiles.</p>
      </div>
      <Explainer id="bio">One simple public page that holds all your important links. Publish it, paste the URL into your Instagram or TikTok bio, and watch which links get clicked.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-5">
        <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium">Public URL {bio.published ? "· live" : "· unpublished"}</p>
            <p className="text-[13px] font-mono truncate" data-testid="bio-public-url">{publicUrl}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="bio-copy-url" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("URL copied"); }} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white transition-colors"><Copy size={14} /></button>
            <a data-testid="bio-preview-link" href={publicUrl} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white transition-colors"><ArrowSquareOut size={14} /></a>
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium mb-1.5">Headline</p>
          <Input data-testid="bio-headline-input" value={bio.headline} onChange={(e) => setBio({ ...bio, headline: e.target.value })} placeholder="e.g. Small-batch coffee, roasted with intent." />
        </div>

        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium">Links</p>
          {bio.links.map((l, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input data-testid={`bio-link-title-${i}`} placeholder="Title" value={l.title} onChange={(e) => setLink(i, "title", e.target.value)} className="max-w-[180px]" />
              <Input data-testid={`bio-link-url-${i}`} placeholder="https://…" value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} />
              <span className="text-[11px] text-slate-400 shrink-0 w-14 text-right">{l.clicks || 0} clicks</span>
              <button data-testid={`bio-link-delete-${i}`} onClick={() => setBio({ ...bio, links: bio.links.filter((_, idx) => idx !== i) })} className="text-slate-300 hover:text-red-500 transition-colors"><Trash size={14} /></button>
            </div>
          ))}
          <button data-testid="bio-add-link" onClick={() => setBio({ ...bio, links: [...bio.links, { title: "", url: "", clicks: 0 }] })}
            className="flex items-center gap-1.5 text-[13px] font-medium text-[hsl(9,55%,48%)] hover:underline"><Plus size={13} /> Add link</button>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <label className="flex items-center gap-3 text-[13px] font-medium">
            <Switch data-testid="bio-published-toggle" checked={bio.published} onCheckedChange={(v) => setBio({ ...bio, published: v })} /> Published
          </label>
          <Button data-testid="bio-save-button" onClick={() => save()} disabled={saving} className="rounded-lg bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
